import { useEffect, useRef, useState } from 'react';
import { Pencil, X } from 'lucide-react';
import { documentService } from '../../../services/documentService';
import './DocumentPreviewPanel.css';

interface DocumentPreviewPanelProps {
  documentId: string | null;
  /** #924 — invoked by the floating Close and Edit controls. The parent
   *  ``GutschriftCheckPage`` wires this to ``setSelectedDocId(null)`` so
   *  both controls share the canvas's single source of truth for
   *  "no document selected" (same end state as clicking the empty
   *  pane). Required, not optional — every render of this panel is
   *  parented by a state holder that can supply the clear callback. */
  onClose: () => void;
}

const PANEL_WIDTH_KEY = 'gutschrift-check.docPreviewWidth';
const PANEL_MIN_WIDTH = 320;
const PANEL_MAX_WIDTH = 1100;
const PANEL_DEFAULT_WIDTH = 520;

function readPersistedWidth(): number {
  if (typeof window === 'undefined') return PANEL_DEFAULT_WIDTH;
  const raw = window.localStorage.getItem(PANEL_WIDTH_KEY);
  if (!raw) return PANEL_DEFAULT_WIDTH;
  const n = Number(raw);
  if (!Number.isFinite(n)) return PANEL_DEFAULT_WIDTH;
  return Math.min(PANEL_MAX_WIDTH, Math.max(PANEL_MIN_WIDTH, n));
}

export default function DocumentPreviewPanel({ documentId, onClose }: DocumentPreviewPanelProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [blobLoading, setBlobLoading] = useState(false);
  const [blobError, setBlobError] = useState<string | null>(null);
  // Mirror ``blobUrl`` into a ref so the unmount-only cleanup useEffect
  // (empty deps, by design) can read the LATEST value rather than the
  // initial-render closure. Without this the hard-unmount path
  // (component dropped from the tree while it holds a live blob URL —
  // route change, parent conditional flip, etc.) saw a stale ``null``
  // and skipped ``URL.revokeObjectURL`` entirely. Repaired as part of
  // #887 alongside the page-level "keep mounted, pass null" change in
  // ``GutschriftCheckPage`` — the ref guards the residual unmount
  // paths the page-level fix doesn't cover.
  const blobUrlRef = useRef<string | null>(null);
  useEffect(() => {
    blobUrlRef.current = blobUrl;
  }, [blobUrl]);
  // Panel width is operator-driven via the left-edge drag handle. The
  // document renderer (PDF iframe with view=FitH, or img sized by width)
  // scales to fill the panel, so widening the panel transparently
  // "zooms in" without explicit +/- controls.
  const [panelWidth, setPanelWidth] = useState<number>(() => readPersistedWidth());
  // Mirror panelWidth into a ref so the pointerup handler can persist
  // the latest committed width without taking a stale closure.
  const panelWidthRef = useRef(panelWidth);
  useEffect(() => {
    panelWidthRef.current = panelWidth;
  }, [panelWidth]);
  // Drag origin (cursor x + panel width at pointerdown). Held in a ref
  // so the three handlers (down/move/up) — separate function bodies on
  // the component, not closures sharing scope — can read consistent
  // start coordinates throughout one drag session. ``null`` outside of
  // an active drag so onMove/onUp can no-op when called spuriously.
  const dragStartRef = useRef<{ x: number; width: number; pointerId: number } | null>(null);
  // The handle element. Captured during pointerdown so the unmount
  // cleanup can release capture if the component goes away mid-drag
  // (otherwise the captured pointer leaks until the next user input).
  const handleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setBlobError(null);
    setFilename(null);
    if (!documentId) {
      setBlobLoading(false);
      return;
    }
    setBlobLoading(true);
    let cancelled = false;
    documentService
      .getDocumentForPreview(documentId)
      .then(({ blobUrl: url, filename: name }) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        setBlobUrl(url);
        setFilename(name);
        setBlobLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        // Log so a user with dev tools open can see the underlying
        // axios/network error — the UI message alone hides it.
        // eslint-disable-next-line no-console
        console.error('[DocumentPreviewPanel] failed to load', documentId, err);
        const status = err?.response?.status;
        let msg: string;
        if (status === 404) msg = `Dokument nicht gefunden (ID ${documentId.slice(0, 8)}…)`;
        else if (status === 401 || status === 403) msg = 'Zugriff auf die Quelldatei verweigert.';
        else if (status) msg = `Vorschau-Fehler (HTTP ${status})`;
        else msg = 'Vorschau konnte nicht geladen werden.';
        setBlobError(msg);
        setBlobLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  // Unmount-only blob revoke. Reads via ``blobUrlRef`` rather than the
  // ``blobUrl`` state directly, because empty-deps effects capture the
  // INITIAL render's closure — that closure always sees ``null`` and
  // would silently skip the revoke (the #887 regression). The ref is
  // updated by the effect above on every ``blobUrl`` change, so the
  // cleanup function always sees the live value at unmount time.
  useEffect(() => {
    return () => {
      const url = blobUrlRef.current;
      if (url) URL.revokeObjectURL(url);
    };
  }, []);

  // Pointer-capture-based drag. Window-level listeners don't work here
  // because the parent document never sees pointer events that happen
  // over the PDF iframe (or the React Flow canvas to the left of the
  // handle) — the iframe consumes them. That left two visible bugs:
  //   • drag froze the moment the cursor crossed onto the iframe and
  //     only resumed when it came back over the handle/panel chrome
  //   • the pointerup that ended the drag was sometimes captured by
  //     the iframe too, leaving the listeners attached and the panel
  //     following the mouse without a button being held (the "click
  //     to toggle resize" symptom)
  // ``setPointerCapture`` on the handle element redirects ALL pointer
  // events for that pointer to the handle until ``releasePointerCapture``
  // (or the pointer disappears), so move/up/cancel reliably fire on
  // the handle regardless of what visual element the cursor is over.
  const onHandlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStartRef.current = {
      x: e.clientX,
      width: panelWidthRef.current,
      pointerId: e.pointerId,
    };
    document.body.style.cursor = 'col-resize';
    // Suppress text selection across the page while the drag is
    // active — without this, dragging across document content
    // selects text and the resize feels janky.
    document.body.style.userSelect = 'none';
  };

  const onHandlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = dragStartRef.current;
    if (!start || start.pointerId !== e.pointerId) return;
    // Handle lives on the panel's LEFT edge: dragging the cursor LEFT
    // (negative dx) must GROW the panel, dragging RIGHT shrinks it —
    // hence subtracting dx from the start width.
    const dx = e.clientX - start.x;
    const next = start.width - dx;
    const clamped = Math.min(PANEL_MAX_WIDTH, Math.max(PANEL_MIN_WIDTH, next));
    setPanelWidth(clamped);
    panelWidthRef.current = clamped;
  };

  const onHandlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = dragStartRef.current;
    if (!start || start.pointerId !== e.pointerId) return;
    dragStartRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // Already released (e.g. pointercancel fired first) — fine.
    }
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    try {
      window.localStorage.setItem(
        PANEL_WIDTH_KEY,
        String(Math.round(panelWidthRef.current)),
      );
    } catch {
      // localStorage may be unavailable (private mode); width still
      // persists for the current session via component state.
    }
  };

  // Unmount-only cleanup: if the component goes away mid-drag, release
  // the captured pointer and restore body styles. Empty deps on purpose
  // — putting handler refs in deps would defeat the point of capture.
  useEffect(() => {
    return () => {
      const start = dragStartRef.current;
      const handle = handleRef.current;
      if (start && handle) {
        try {
          handle.releasePointerCapture(start.pointerId);
        } catch {
          // Pointer already released; nothing to do.
        }
      }
      if (start) {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
  }, []);

  // #835 — when no document is selected the entire panel unmounts so
  // the flex-sibling canvas (``gutschrift-check-page__canvas``, ``flex:
  // 1 1 auto``) reflows to the full viewport width. Pre-#835 the panel
  // rendered a ``Wähle ein Dokument…`` empty-state hint at its 520px
  // default width, wasting ~50% of viewport on a state the operator
  // had already seen. Returning ``null`` here (rather than collapsing
  // via CSS) keeps the resize-handle pointer-capture lifecycle simple:
  // there is no half-mounted aside that owns a stale captured pointer
  // when ``documentId`` drops to null. The page-level conditional in
  // ``GutschriftCheckPage`` is intentionally redundant — both shapes
  // need to hold so the panel reliably disappears.
  if (!documentId) {
    return null;
  }

  const isPdf = filename?.toLowerCase().endsWith('.pdf');

  return (
    <aside
      className="doc-preview-panel"
      data-testid="doc-preview-panel"
      style={{ width: `${panelWidth}px`, flex: `0 0 ${panelWidth}px` }}
    >
      <div
        ref={handleRef}
        className="doc-preview-panel__resize-handle"
        role="separator"
        aria-orientation="vertical"
        aria-label="Vorschau-Breite anpassen"
        onPointerDown={onHandlePointerDown}
        onPointerMove={onHandlePointerMove}
        onPointerUp={onHandlePointerUp}
        onPointerCancel={onHandlePointerUp}
      />
      {/* #924 — floating Close + Edit controls anchored top-right.
        * z-index sits above the embedded iframe (which has no
        * explicit z-index) AND above the resize handle (z-index: 3)
        * so a panel-edge drag never lands on the controls. The
        * resize handle owns the LEFT edge so top-right is free.
        * Both controls converge on ``onClose`` — the canvas's single
        * source of truth for "no document selected". The Edit
        * anchor uses a real ``<a target="_blank">`` so middle-click
        * and cmd-click still open the new tab; ``onClick`` runs
        * synchronously before the browser navigates, so even the
        * modifier-click paths land in the deselected state. */}
      <div className="doc-preview-panel__floating-controls">
        <a
          className="doc-preview-panel__icon-button"
          // #1175 — ``mode=edit`` tells DocumentsPage to open the document
          // straight into the preview/edit overlay (correction-endpoint
          // path) rather than only expanding its feed row, so the operator
          // can start editing facts immediately.
          href={`/documents?doc=${encodeURIComponent(documentId)}&mode=edit`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
          aria-label="Im Dokument-Editor öffnen"
          title="Im Dokument-Editor öffnen"
        >
          <Pencil size={16} aria-hidden />
        </a>
        <button
          type="button"
          className="doc-preview-panel__icon-button"
          onClick={onClose}
          aria-label="Vorschau schließen"
          title="Vorschau schließen"
        >
          <X size={16} aria-hidden />
        </button>
      </div>
      {blobLoading && (
        <div className="doc-preview-panel__hint">Lade Vorschau…</div>
      )}
      {blobError && (
        <div className="doc-preview-panel__hint doc-preview-panel__hint--error">
          {blobError}
        </div>
      )}
      {blobUrl && (
        <div className="doc-preview-panel__stage">
          {isPdf ? (
            // ``view=FitH`` tells the embedded PDF viewer to fit the
            // page width to the iframe — widening the panel widens
            // the rendered page, and the viewer's own scrollbar handles
            // vertical paging when the page grows taller than the
            // iframe. Chrome chrome (toolbar/navpanes/scrollbar) hidden
            // for a clean look; the internal scrollbar still renders.
            <iframe
              key={blobUrl}
              className="doc-preview-panel__frame"
              src={`${blobUrl}#toolbar=0&navpanes=0&view=FitH`}
              title="Dokumentvorschau"
            />
          ) : (
            // Width-fit, auto height — the natural aspect ratio is
            // preserved, so widening the panel grows the image taller
            // too. ``overflow-y: auto`` on the stage shows a scrollbar
            // when that height exceeds the panel.
            <img className="doc-preview-panel__image" src={blobUrl} alt="" />
          )}
        </div>
      )}
    </aside>
  );
}
